import { useState, useEffect, useRef } from "react";
import { toast } from "../../components/AppToast";
import { createTenantSetting, updateTenantSetting, getTenantSettings } from "../../services/tenant/tenant";
import ConfirmDialog from "../../components/ConfirmDialog";

// ✅ API URL options with both code + label
const urlOptions = [
  { code: "item", label: "Item Master" },
  { code: "inventory", label: "Inventory" },
  { code: "po", label: "Purchase Order" },
  { code: "asn", label: "ASN (Advance Shipment Notice)" },
  { code: "grn", label: "Goods Receipt" },
  // { code: "invoicing", label: "Invoicing" },
  { code: "ShipmentOrder", label: "Sales Order" },
  {code: "LOAD", label: "Shipment Order"},
  // { code: "returns", label: "Returns" },
  { code: "CYCLECOUNT", label: "Cycle Count" },
  { code: "Owner", label: "Partner" },
  {code:"Adjustment", label: "Adjustment"},
  {code:"Location", label: "Location"},
  {code:"Appointment", label: "Appointment"},
  { code: "SYNC-GRN", label: "Sync GRN" },
  { code: "SYNC-ITEM", label: "Sync SKU" },
  { code: "SYNC-PO", label: "Sync Purchase Order" },
  { code: "SYNC-ASN", label: "Sync ASN" },
  { code: "SYNC-SALESORDER", label: "Sync Sales Order" },
];

// ✅ Map form field names to human-friendly labels
const formatLabel = (field) => {
  const mapping = {
    cn: "Company Name",
    accessUrl: "Access URL",
    clientId: "Client ID",
    clientSecret: "Client Secret",
    username: "Username",
    password: "Password",
  };
  return mapping[field] || field;
};

const TenantCard = ({ initialConfig = null, tenantId, onSaved }) => {
  // start in edit mode if we already have server config
  const [editTenant, setEditTenant] = useState(Boolean(initialConfig));
  const originalConfigRef = useRef(null); // snapshot used for change detection
  const pendingSavePayloadRef = useRef(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [config, setConfig] = useState(() => ({
    cn: "",
    accessUrl: "",
    clientId: "",
    clientSecret: "",
    username: "",
    password: "",
    token: null,
    status: 1,
    // include sync flags for each row (default ON)
    urls: [{ name: "item", url: "", status: 1, syncTo: true, syncFrom: true }],
    ...(initialConfig || {})
  }));

  // If initialConfig changes after mount, merge it into local state
  useEffect(() => {
    if (!initialConfig) return;
    setConfig(prev => {
      // merge arrays carefully: prefer server urls if present
      const merged = { ...prev, ...initialConfig };
      if (Array.isArray(initialConfig.urls)) {
        merged.urls = initialConfig.urls.map(u => ({
          name: u.name || "",
          url: u.url || "",
          status: u.status ?? 1,
          // treat undefined as ON (default ON), preserve explicit false
          syncTo: u.syncTo === undefined ? true : Boolean(u.syncTo),
          syncFrom: u.syncFrom === undefined ? true : Boolean(u.syncFrom),
          _id: u._id
        }));
      }
      return merged;
    });
    // capture snapshot for change detection (deep clone)
    try { originalConfigRef.current = JSON.parse(JSON.stringify({ ...initialConfig })); } catch { originalConfigRef.current = { ...initialConfig }; }
    // keep edit mode as before
    setEditTenant(true);
  }, [initialConfig]);

  // Initialize Bootstrap tooltips (same approach used in CreateFacility)
  useEffect(() => {
    setTimeout(() => {
      try {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        [...tooltipTriggerList].forEach(el => {
          if (window?.bootstrap && typeof window.bootstrap.Tooltip === "function") {
            // eslint-disable-next-line no-new
            new window.bootstrap.Tooltip(el);
          }
        });
      } catch (err) {
        // silent if bootstrap not available
      }
    }, 150);
  }, []);

  const [errors, setErrors] = useState({
    tenantFields: {},
    urls: [],
  });
  const [visibleFields, setVisibleFields] = useState({});

  const notifySaved = (title, message) => {
    if (onSaved) {
      onSaved({ title, message });
      return;
    }
    toast.success(message);
  };

  // Helper: resolve a url option code to its human label (fallbacks to code/url/API)
  const getLabelForCode = (code, urlCandidate) => {
    if (!code && !urlCandidate) return "API";
    const found = urlOptions.find((o) => o.code === code);
    if (found) return found.label;
    if (code) return code;
    try {
      // fallback show hostname if url provided
      const u = new URL(String(urlCandidate));
      return u.hostname;
    } catch {
      return urlCandidate || "API";
    }
  };

  // Toggle sync-to flag
  const handleToggleSyncTo = (index) => {
    const updatedUrls = [...config.urls];
    const row = { ...(updatedUrls[index] || {}) };
    row.syncTo = !row.syncTo;
    updatedUrls[index] = row;
    setConfig({ ...config, urls: updatedUrls });
    toast.info(`${getLabelForCode(row.name, row.url)}: Sync to 3rd Party System Turned  ${row.syncTo ? "ON" : "OFF"}`);
  };

  // Toggle sync-from flag
  const handleToggleSyncFrom = (index) => {
    const updatedUrls = [...config.urls];
    const row = { ...(updatedUrls[index] || {}) };
    row.syncFrom = !row.syncFrom;
    updatedUrls[index] = row;
    setConfig({ ...config, urls: updatedUrls });
    toast.info(`${getLabelForCode(row.name, row.url)}: Sync from 3rd Party System Turned  ${row.syncFrom ? "ON" : "OFF"}`);
  };

  const handleTenantChange = (e) => {
    const { name, value } = e.target;
    setConfig({ ...config, [name]: value });
    setErrors((prev) => ({
      ...prev,
      tenantFields: { ...prev.tenantFields, [name]: "" },
    }));
  };

  const handleUrlChangeOLD = (index, field, value) => {
    const updatedUrls = [...config.urls];
    updatedUrls[index][field] = value;
    setConfig({ ...config, urls: updatedUrls });

    const updatedErrors = [...errors.urls];
    if (!updatedErrors[index]) updatedErrors[index] = {};
    updatedErrors[index][field] = "";
    setErrors({ ...errors, urls: updatedErrors });
  };


  const handleUrlChange= (index, field, value) => {
    const updatedUrls = [...config.urls];
    const row = { ...(updatedUrls[index] || {}) };

    row[field] = value;

    // ✅ ONLY ASN CASE FIX
    if (field === "name" && value === "asn") {
      row.syncFrom = false; 
    }

    updatedUrls[index] = row;
    setConfig({ ...config, urls: updatedUrls });

    const updatedErrors = [...errors.urls];
    if (!updatedErrors[index]) updatedErrors[index] = {};
    updatedErrors[index][field] = "";
    setErrors({ ...errors, urls: updatedErrors });
  };


  const handleAddUrl = () => {
    // new rows default to ON
    setConfig({ ...config, urls: [...config.urls, { name: "", url: "", status: 1, syncTo: true, syncFrom: true }] });
    setErrors((prev) => ({
      ...prev,
      urls: [...prev.urls, {}],
    }));
  };

  const handleRemoveUrl = (index) => {
    const updatedUrls = config.urls.filter((_, i) => i !== index);
    setConfig({ ...config, urls: updatedUrls });

    const updatedErrors = errors.urls.filter((_, i) => i !== index);
    setErrors({ ...errors, urls: updatedErrors });
  };

  const getAvailableOptions = (currentIndex) => {
    const selectedNames = config.urls.map((u, i) => (i !== currentIndex ? u.name : null));
    return urlOptions.filter((opt) => !selectedNames.includes(opt.code));
  };

  // ✅ Save with inline validation
  const handleSaveTenantConfig = async () => {
    const tenantFields = ["cn", "accessUrl", "clientId", "clientSecret", "username", "password"];
    const tenantErrors = {};
    let hasError = false;

    // Validate tenant fields
    for (let field of tenantFields) {
      if (!config[field] || config[field].trim() === "") {
        tenantErrors[field] = `${formatLabel(field)} is required`;
        hasError = true;
      }
    }

    // Validate URLs
    const urlErrors = config.urls.map((urlObj) => {
      const obj = {};
      if (!urlObj.name) {
        obj.name = "Select API";
        hasError = true;
      }
      // if (!urlObj.url || urlObj.url.trim() === "") {
      //   obj.url = "Enter URL";
      //   hasError = true;
      // }
      return obj;
    });

    setErrors({ tenantFields: tenantErrors, urls: urlErrors });

    if (!hasError) {
      // Build payload to send to backend (example shape)
      const payload = {
        tenantId:  config.tenantId || config?.tenant?._id || config?.tenantId, // keep tenantId if present
        cn: config.cn,
        accessUrl: config.accessUrl,
        clientId: config.clientId,
        clientSecret: config.clientSecret,
        username: config.username,
        password: config.password,
        token: config.token || null,
        status: config.status,
        urls: (config.urls || []).map(u => ({
          name: u.name || "",
          url: u.url || "",
          status: u.status === undefined ? 1 : u.status,
          // default ON when undefined
          syncTo: u.syncTo === undefined ? true : Boolean(u.syncTo),
          syncFrom: u.syncFrom === undefined ? true : Boolean(u.syncFrom),
           // _id: u._id
         }))
       };

      try {
        // helper to perform actual save + refresh
        const performSave = async (pl) => {
          try {
            let resp;
            if (pl.tenantId) {
              resp = await updateTenantSetting(pl);
              notifySaved("Connection updated", "Tenant credentials and API routes are saved.");
            } else {
              pl.tenantId = tenantId;
              resp = await createTenantSetting(pl);
              notifySaved("Connection created", "This tenant can now sync through the saved endpoints.");
            }
  
            const resolvedTenantId =
              pl.tenantId ||
              resp?.tenantId ||
              resp?.data?.tenantId ||
              resp?._id ||
              resp?.data?._id;
  
            if (resolvedTenantId) {
              try {
                const fresh = await getTenantSettings(resolvedTenantId);
                const data = fresh?.data ?? fresh;
                if (data) {
                  const merged = { ...pl, ...data };
                  if (Array.isArray(data.urls)) {
                    merged.urls = data.urls.map(u => ({
                      name: u.name || "",
                      url: u.url || "",
                      status: u.status ?? 1,
                      syncTo: u.syncTo === undefined ? true : Boolean(u.syncTo),
                      syncFrom: u.syncFrom === undefined ? true : Boolean(u.syncFrom),
                      _id: u._id
                    }));
                  }
                  merged.tenantId = data.tenantId ?? data._id ?? merged.tenantId;
                  setConfig(merged);
                  try { originalConfigRef.current = JSON.parse(JSON.stringify(merged)); } catch { originalConfigRef.current = merged; }
                }
              } catch (err) {
                console.warn("Failed to refresh tenant settings after save:", err);
              }
            }
  
            setEditTenant(true);
          } catch (err) {
            console.error("Save tenant settings failed:", err);
            const message = (err && (err.message || err?.error || JSON.stringify(err))) || "Save failed";
            toast.error(message);
          }
        };

        // detect changes vs original snapshot
        const originalJson = originalConfigRef.current ? JSON.stringify(originalConfigRef.current) : null;
        const currentJson = (() => { try { return JSON.stringify(payload); } catch { return null; } })();

        if (originalJson && currentJson && originalJson !== currentJson) {
          // changes detected — request confirmation
          pendingSavePayloadRef.current = payload;
          setShowConfirm(true);
        } else {
          // no change — perform save directly
          await performSave(payload);
        }
      } catch (err) {
        console.error("Save tenant settings failed:", err);
        const message = (err && (err.message || err?.error || JSON.stringify(err))) || "Save failed";
        toast.error(message);
      }
    }
  };

  // Confirm dialog handlers
  const onConfirmSave = async () => {
    setShowConfirm(false);
    const pl = pendingSavePayloadRef.current;
    if (!pl) return;
    pendingSavePayloadRef.current = null;
    // reuse perform logic (duplicated minimal flow)
    try {
      let resp;
      if (pl.tenantId) {
        resp = await updateTenantSetting(pl);
        notifySaved("Connection updated", "Tenant credentials and API routes are saved.");
      } else {
        pl.tenantId = tenantId;
        resp = await createTenantSetting(pl);
        notifySaved("Connection created", "This tenant can now sync through the saved endpoints.");
      }

      const resolvedTenantId =
        pl.tenantId ||
        resp?.tenantId ||
        resp?.data?.tenantId ||
        resp?._id ||
        resp?.data?._id;

      if (resolvedTenantId) {
        try {
          const fresh = await getTenantSettings(resolvedTenantId);
          const data = fresh?.data ?? fresh;
          if (data) {
            const merged = { ...pl, ...data };
            if (Array.isArray(data.urls)) {
              merged.urls = data.urls.map(u => ({
                name: u.name || "",
                url: u.url || "",
                status: u.status ?? 1,
                syncTo: u.syncTo === undefined ? true : Boolean(u.syncTo),
                syncFrom: u.syncFrom === undefined ? true : Boolean(u.syncFrom),
                _id: u._id
              }));
            }
            merged.tenantId = data.tenantId ?? data._id ?? merged.tenantId;
            setConfig(merged);
            try { originalConfigRef.current = JSON.parse(JSON.stringify(merged)); } catch { originalConfigRef.current = merged; }
          }
        } catch (err) {
          console.warn("Failed to refresh tenant settings after save:", err);
        }
      }
      setEditTenant(true);
    } catch (err) {
      console.error("Save tenant settings failed:", err);
      const message = (err && (err.message || err?.error || JSON.stringify(err))) || "Save failed";
      toast.error(message);
    }
  };

  const onCancelSave = () => {
    setShowConfirm(false);
    pendingSavePayloadRef.current = null;
  };

  const secretField = (field) => field === "password" || field === "clientSecret";

  return (
    <>
      <div className="admin-block">
        <div className="admin-toolbar">
          <p>Keep these credentials private. They authenticate this tenant with the connected system.</p>
          <button type="button" className="settings-btn settings-btn--ghost" onClick={() => setEditTenant(!editTenant)}>
            <i className={`bi ${editTenant ? "bi-eye" : "bi-pencil"}`} />
            {editTenant ? "Hide form" : "Edit connection"}
          </button>
        </div>

        {editTenant ? (
          <>
            <div className="settings-grid">
              {["cn", "accessUrl", "clientId", "clientSecret", "username", "password"].map((field) => (
                <div className="settings-field" key={field}>
                  <label className="settings-field__label" htmlFor={`tenant-${field}`}>
                    {formatLabel(field)}
                    {field === "clientSecret" ? (
                      <em
                        title="A confidential key used with the Client ID. Do not share it."
                      >
                        Private
                      </em>
                    ) : null}
                  </label>
                  <div className={secretField(field) ? "settings-input-wrap" : undefined}>
                    <input
                      id={`tenant-${field}`}
                      type={secretField(field) && !visibleFields[field] ? "password" : "text"}
                      name={field}
                      value={config[field] || ""}
                      onChange={handleTenantChange}
                      className={`settings-input${errors.tenantFields[field] ? " is-invalid" : ""}`}
                      placeholder={`Enter ${formatLabel(field).toLowerCase()}`}
                      autoComplete="off"
                    />
                    {secretField(field) ? (
                      <button
                        type="button"
                        className="settings-eye"
                        onClick={() => setVisibleFields((current) => ({ ...current, [field]: !current[field] }))}
                        aria-label={visibleFields[field] ? `Hide ${formatLabel(field)}` : `Show ${formatLabel(field)}`}
                      >
                        <i className={`bi ${visibleFields[field] ? "bi-eye-slash" : "bi-eye"}`} />
                      </button>
                    ) : null}
                  </div>
                  {errors.tenantFields[field] ? (
                    <span className="settings-error">{errors.tenantFields[field]}</span>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="admin-urls">
              <div className="admin-toolbar">
                <div>
                  <h3>API routes</h3>
                  <p>Map each module to an endpoint, then choose whether data is pushed or pulled.</p>
                </div>
                <button type="button" className="settings-btn settings-btn--ghost" onClick={handleAddUrl}>
                  <i className="bi bi-plus-lg" />
                  Add route
                </button>
              </div>

              <div className="admin-url-list">
                {config.urls.map((urlObj, index) => {
                  const pushLocked = ["item", "inventory", "grn"].includes(urlObj.name);
                  const pullLocked = ["asn"].includes(urlObj.name);
                  return (
                    <div className="admin-url-row" key={`${urlObj._id || "url"}-${index}`}>
                      <div className="settings-field">
                        <label className="settings-field__label" htmlFor={`api-name-${index}`}>Module</label>
                        <select
                          id={`api-name-${index}`}
                          className={`settings-input${errors.urls[index]?.name ? " is-invalid" : ""}`}
                          value={urlObj.name}
                          onChange={(e) => handleUrlChange(index, "name", e.target.value)}
                        >
                          <option value="">Select API</option>
                          {getAvailableOptions(index).map((opt) => (
                            <option key={opt.code} value={opt.code}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {errors.urls[index]?.name ? (
                          <span className="settings-error">{errors.urls[index].name}</span>
                        ) : null}
                      </div>

                      <div className="settings-field">
                        <label className="settings-field__label" htmlFor={`api-url-${index}`}>Endpoint</label>
                        <input
                          id={`api-url-${index}`}
                          type="text"
                          className={`settings-input${errors.urls[index]?.url ? " is-invalid" : ""}`}
                          placeholder="https://"
                          value={urlObj.url}
                          onChange={(e) => handleUrlChange(index, "url", e.target.value)}
                          disabled={["inventory", "grn"].includes(urlObj.name)}
                        />
                      </div>

                      <button
                        type="button"
                        className={`admin-sync${urlObj.syncTo ? " is-on" : ""}`}
                        onClick={() => handleToggleSyncTo(index)}
                        disabled={pushLocked}
                        aria-pressed={Boolean(urlObj.syncTo)}
                        title="Sync to third party"
                      >
                        <i className="bi bi-cloud-upload" />
                        Push
                      </button>
                      <button
                        type="button"
                        className={`admin-sync${urlObj.syncFrom ? " is-on" : ""}`}
                        onClick={() => handleToggleSyncFrom(index)}
                        disabled={pullLocked}
                        aria-pressed={Boolean(urlObj.syncFrom)}
                        title="Sync from third party"
                      >
                        <i className="bi bi-cloud-download" />
                        Pull
                      </button>
                      <button
                        type="button"
                        className="admin-remove"
                        onClick={() => handleRemoveUrl(index)}
                        disabled={config.urls.length === 1}
                        aria-label="Remove route"
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="settings-actions">
              <button type="button" className="settings-btn settings-btn--primary" onClick={handleSaveTenantConfig}>
                Save connection
              </button>
            </div>
          </>
        ) : (
          <p className="settings-footnote">The connection form is hidden. Open it when you need to change credentials or routes.</p>
        )}
      </div>

      {/* Confirm dialog shown when changes detected before saving */}
      <ConfirmDialog
        open={showConfirm}
        onClose={onCancelSave}
        onConfirm={onConfirmSave}
        title="Save changes?"
        message="Tenant Settings updated. Would you like to save these changes?"
        yesText="Yes"
        noText="No"
      />
    </>
  );
};

export default TenantCard;
